import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem, springPresets } from '@/lib/motion';
import { CISP_RUN_LOG } from '@/data/index';
import { SectionHeader, FlagChip } from '@/components/Badges';
import { TierBadge, VerdictBadge, SeedBadge } from '@/components/Badges';
import { ChevronDown, ChevronUp, ClipboardList, AlertCircle } from 'lucide-react';
import type { CISPRunEntry } from '@/lib/index';

function RunRow({ run, index }: { run: CISPRunEntry; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const isEscalated = run.tier === 'TIER-3' || run.tier === 'TIER-4';

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07, ...springPresets.gentle }}
      className={`rounded-lg border transition-colors duration-150 overflow-hidden ${
        isEscalated
          ? 'border-red-800/40 bg-red-950/10'
          : run.seed_type === 'clean'
          ? 'border-emerald-800/20 bg-card'
          : 'border-amber-800/20 bg-card'
      }`}
    >
      {/* Row Summary */}
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-muted/20 transition-colors"
      >
        {/* Run ID */}
        <span className="font-mono text-xs text-muted-foreground w-36 shrink-0">
          {run.run_id}
        </span>

        {/* Session Name */}
        <span className="font-medium text-sm text-foreground flex-1 text-left">
          {run.session_name}
        </span>

        {/* Date */}
        <span className="font-mono text-xs text-muted-foreground hidden md:block w-24 shrink-0">
          {run.assessment_date}
        </span>

        {/* Badges */}
        <div className="flex items-center gap-2 shrink-0">
          <SeedBadge seedType={run.seed_type} size="sm" />
          <VerdictBadge verdict={run.absolute_verdict} size="sm" />
          <TierBadge tier={run.tier} size="sm" />
        </div>

        {/* Escalation indicator */}
        {isEscalated && (
          <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />
        )}

        {/* Expand toggle */}
        <span className="text-muted-foreground shrink-0">
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </span>
      </button>

      {/* Expanded Detail Panel */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springPresets.gentle}
            className="overflow-hidden"
          >
            <div className="border-t border-border/50 px-5 py-5 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left: Metadata */}
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-2">
                    Session Metadata
                  </div>
                  <dl className="space-y-1.5 font-mono text-xs">
                    {[
                      { key: 'Run ID', val: run.run_id },
                      { key: 'Model Tag', val: run.model_tag },
                      { key: 'Pipeline', val: run.pipeline_version },
                      { key: 'Assessment Date', val: run.assessment_date },
                      { key: 'Retro-Entry Date', val: run.retro_entry_date },
                    ].map(({ key, val }) => (
                      <div key={key} className="flex gap-2">
                        <dt className="text-muted-foreground w-36 shrink-0">{key}</dt>
                        <dd className="text-foreground">{val}</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                {/* Flags */}
                <div>
                  <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-2">
                    Flags
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {run.flags.map((flag) => (
                      <FlagChip key={flag} flag={flag} />
                    ))}
                  </div>
                </div>

                {/* Retro status */}
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 text-[10px] font-mono rounded border border-emerald-700/40 bg-emerald-950/40 text-emerald-400">
                    ✓ RETRO-ENTERED
                  </span>
                  <span className="text-xs font-mono text-muted-foreground">
                    {run.retro_entry_date}
                  </span>
                </div>
              </div>

              {/* Right: Notes */}
              <div>
                <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-2">
                  Analyst Notes
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed">{run.notes}</p>

                {isEscalated && (
                  <div className="mt-4 flex items-start gap-2 p-3 rounded bg-red-950/30 border border-red-800/30">
                    <AlertCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-red-300 font-mono leading-relaxed">
                      {run.tier === 'TIER-3'
                        ? 'TIER-3: Analyst escalation required. Pattern divergence from control run detected. Cross-reference with GG-CSAP-APR-001 before downstream use.'
                        : 'TIER-4: Mandatory manual review. Session output may not be used until fully classified.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function CISPLogPage() {
  const totalRuns = CISP_RUN_LOG.length;
  const retroCount = CISP_RUN_LOG.filter((r) => r.retro_entered).length;
  const escalated = CISP_RUN_LOG.filter((r) => r.tier === 'TIER-3' || r.tier === 'TIER-4').length;

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Header */}
      <motion.div variants={staggerItem}>
        <SectionHeader
          tag="CISP Run Log · April Assessment"
          title="Retro-Entered Session Records"
          subtitle="All 4 April Assessment runs retro-entered with GG-CSAP v1.0 codebook tier assignments. Click any row to expand analyst notes and full metadata."
        />

        {/* Summary chips */}
        <div className="flex flex-wrap gap-3 mt-3">
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card border border-border text-xs font-mono text-foreground">
            <ClipboardList className="w-3.5 h-3.5 text-primary" />
            {totalRuns} total runs
          </span>
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-950/40 border border-emerald-800/30 text-xs font-mono text-emerald-400">
            ✓ {retroCount}/{totalRuns} retro-entered
          </span>
          {escalated > 0 && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-950/40 border border-red-800/30 text-xs font-mono text-red-400">
              ⚠ {escalated} escalation{escalated > 1 ? 's' : ''} pending
            </span>
          )}
        </div>
      </motion.div>

      {/* Column Headers */}
      <motion.div variants={staggerItem}>
        <div className="flex items-center gap-3 px-5 py-2 text-[10px] font-mono text-muted-foreground uppercase tracking-widest border-b border-border">
          <span className="w-36 shrink-0">Run ID</span>
          <span className="flex-1">Session Name</span>
          <span className="hidden md:block w-24 shrink-0">Date</span>
          <span className="shrink-0">Seed · Verdict · Tier</span>
          <span className="w-8" />
        </div>
      </motion.div>

      {/* Run Rows */}
      <motion.div variants={staggerItem} className="space-y-2">
        {CISP_RUN_LOG.map((run, idx) => (
          <RunRow key={run.run_id} run={run} index={idx} />
        ))}
      </motion.div>

      {/* Log Footer */}
      <motion.div variants={staggerItem}>
        <div className="rounded-lg border border-border bg-card/50 px-5 py-4">
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 text-xs font-mono text-muted-foreground">
            <span>Log version: <span className="text-foreground">GG-CSAP v1.0</span></span>
            <span>Codebook: <span className="text-foreground">April 2026</span></span>
            <span>Last retro-entry: <span className="text-foreground">2026-04-07</span></span>
            <span className="md:ml-auto text-[10px] text-muted-foreground/60">
              All entries verified against April Model Assessment schema. Tier assignments final unless overridden by analyst review.
            </span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
